/**
 * AssigneeAvatar — displays responsible person avatar with click-to-assign dropdown.
 * Props:
 *   responsible      {object|null}  — { id, name, avatar_url } or null if unassigned
 *   people           {array}        — list of { id, name, avatar_url } available for assignment
 *   conversationId   {number}       — conversation id for PATCH request
 *   onAssigned       {function}     — callback(personId|null) after assignment
 *   size             {number}       — pixel size (default 32)
 */

import { h } from 'preact'
import { useState } from 'preact/hooks'
import { patch } from '@rails/request.js'

export function AssigneeAvatar({ responsible, people = [], conversationId, onAssigned, size = 32 }) {
  const [open,   setOpen]   = useState(false)
  const [saving, setSaving] = useState(false)

  async function handleAssign(userId) {
    setSaving(true)
    setOpen(false)
    try {
      const res = await patch(
        `/admin/support/conversations/${conversationId}/assign_responsible`,
        {
          body: JSON.stringify({ user_id: userId }),
          contentType: 'application/json',
          responseKind: 'json',
        }
      )
      if (res.ok && onAssigned) {
        const data = await res.json
        onAssigned(data)
      }
    } catch (err) {
      console.error('[AssigneeAvatar] assign error', err)
    } finally {
      setSaving(false)
    }
  }

  const initials = responsible
    ? (responsible.name || '?').trim()[0]?.toUpperCase() || '?'
    : null

  return (
    <div class="assignee-wrapper">
      <button
        type="button"
        class={`assignee-btn${responsible ? ' assigned' : ''}`}
        style={{ width: `${size}px`, height: `${size}px`, fontSize: `${Math.round(size * 0.38)}px` }}
        title={responsible ? `Felelős: ${responsible.name}` : 'Felelős kiosztása'}
        onClick={() => setOpen(!open)}
        disabled={saving}
      >
        {responsible
          ? (responsible.avatar_url
            ? <img src={responsible.avatar_url} width={size} height={size} alt={responsible.name} loading="lazy" />
            : initials
          )
          : <i class="bi bi-person-plus" />
        }
      </button>

      {open && (
        <div
          class="dropdown-menu show shadow-sm py-1 assignee-dropdown"
        >
          <button
            type="button"
            class="dropdown-item d-flex align-items-center gap-2 small text-muted"
            onClick={() => handleAssign(null)}
          >
            <i class="bi bi-person-dash" />
            Kiosztás törlése
          </button>
          <hr class="dropdown-divider my-1" />
          {people.map(person => (
            <button
              key={person.id}
              type="button"
              class={`dropdown-item d-flex align-items-center gap-2 small${responsible?.user_id === person.id ? ' fw-semibold' : ''}`}
              onClick={() => handleAssign(person.id)}
            >
              {person.avatar_url
                ? <img src={person.avatar_url} width={22} height={22} class="rounded-circle" alt={person.name} />
                : (
                  <div class="assignee-dropdown-avatar">
                    {(person.name || '?')[0]?.toUpperCase()}
                  </div>
                )
              }
              <span>{person.name}</span>
              {responsible?.user_id === person.id && <i class="bi bi-check ms-auto text-primary" />}
            </button>
          ))}
          {people.length === 0 && (
            <span class="dropdown-item-text text-muted small">Nincs elérhető személy</span>
          )}
        </div>
      )}

    </div>
  )
}
